import React, { useState } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import Card from "../../../../componentLibrary/Card";
import Input from "../../../../componentLibrary/Input";
import DraggableComponentCard from "../DraggableComponentCard";
import Combobox, {
  ComboboxOptionI,
} from "../../../../componentLibrary/Combobox";
import { ComponentI, ComponentEnum } from "../../../../types";
import { SelectedComponentType } from "../../utils/selectedComponentUtils";
import {
  getNewComponentContent,
  transferContentVariables,
} from "../../utils/addComponent";
import {
  COMPONENT_GROUPS,
  getComponentGroup,
} from "../../utils/componentGroup";

export default function ToggleContentCard({
  component,
  handleContentChange,
  variableUptoCurrentPage,
  provided,
  selectedComponent,
  handleComponentClick,
  componentRefs,
}: {
  component: ComponentI;
  handleContentChange: (
    id: string,
    field: string,
    value: string | boolean | ComponentI[],
  ) => void;
  variableUptoCurrentPage: string[];
  provided: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  selectedComponent: SelectedComponentType;
  handleComponentClick: (selectedComponent: SelectedComponentType) => void;
  componentRefs: React.RefObject<{ [key: string]: HTMLDivElement | null }>;
}) {
  const [minimizedComponents, setMinimizedComponents] = React.useState<{
    [key: string]: boolean;
  }>({});
  const getIsMinimized = (componentId: string) => {
    return minimizedComponents[componentId] ?? false;
  };
  const setIsMinimized = (componentId: string, isMinimized: boolean) => {
    setMinimizedComponents((prev) => ({ ...prev, [componentId]: isMinimized }));
  };

  const [componentsList, setcomponentsList] = useState<ComponentI[]>(
    component?.content?.componentsList ?? [],
  );

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    const reorderedComponents = Array.from(componentsList);
    const [movedComponent] = reorderedComponents.splice(source.index, 1);
    reorderedComponents.splice(destination.index, 0, movedComponent);
    setcomponentsList(reorderedComponents);
  };

  const removeComponent = (componentId: string) => {
    const updatedComponents = componentsList.filter(
      (c) => c._id !== componentId,
    );
    setcomponentsList(updatedComponents);
    handleContentChange(component._id, "componentsList", updatedComponents);
  };

  const addSelectedComponent = (option: ComboboxOptionI) => {
    if (!option) return;

    const componentType = option.value as ComponentEnum;
    const content = getNewComponentContent(componentType);

    // Prevent nesting "Other" components (LOGIC_COMPONENT, FOR_EACH, TOGGLE) inside Toggle
    if (
      componentType === ComponentEnum.LOGIC_COMPONENT ||
      componentType === ComponentEnum.FOR_EACH ||
      componentType === ComponentEnum.TOGGLE
    ) {
      return;
    }

    const newComponent: ComponentI = {
      _id: `${componentType}_${Date.now()}`,
      type: componentType,
      content,
    };

    let insertIndex = componentsList.length;
    if (selectedComponent) {
      const selectedIndex = componentsList.findIndex(
        (comp) => comp._id === selectedComponent.component?._id,
      );
      if (selectedIndex !== -1) {
        insertIndex = selectedIndex + 1;
      }
    }
    const updatedComponents = [
      ...componentsList.slice(0, insertIndex),
      newComponent,
      ...componentsList.slice(insertIndex),
    ];
    setcomponentsList(updatedComponents);
    handleContentChange(component._id, "componentsList", updatedComponents);
  };

  const updatecomponentsList = (
    id: string,
    field: string,
    value: string | undefined,
  ) => {
    setcomponentsList((prevcomponentsList) => {
      const newcomponentsList = prevcomponentsList.map((component) => {
        if (component._id === id) {
          const updatedContent = { ...component.content };

          if (value === undefined) {
            delete updatedContent[field];
          } else {
            updatedContent[field] = value;
          }

          return { ...component, content: updatedContent };
        }
        return component;
      });
      handleContentChange(component._id, "componentsList", newcomponentsList);
      return newcomponentsList;
    });
  };

  const handleTypeChange = (componentId: string, value: ComponentEnum) => {
    setcomponentsList((prevcomponentsList) => {
      const newcomponentsList = prevcomponentsList.map((component) => {
        if (component._id === componentId) {
          const newContent = getNewComponentContent(value);
          // Transfer variables from old content to new content based on COMPONENT_GROUPS
          const transferredContent = transferContentVariables(
            component.content,
            newContent,
            component.type,
            value,
            COMPONENT_GROUPS,
          );
          return {
            ...component,
            type: value,
            content: transferredContent,
          };
        }
        return component;
      });
      handleContentChange(component._id, "componentsList", newcomponentsList);
      return newcomponentsList;
    });
  };

  return (
    <Card>
      <div className="space-y-6">
        <Input
          value={component.content.title || ""}
          onChange={(e) =>
            handleContentChange(component._id, "title", e.target.value)
          }
          label="Toggle Title"
          placeholder="Enter the title for the toggle section..."
        />

        {/* Components Section */}
        <div className="space-y-2">
          <label className="font-semibold">
            Component/s: <span className="text-red-500"> *</span>
          </label>
          <div
            className="flex items-center space-x-2"
            data-rbd-drag-handle-context-id={
              provided.dragHandleProps?.["data-rbd-drag-handle-context-id"]
            }
            data-rbd-drag-handle-draggable-id="gibberish2"
          >
            <Combobox
              key={componentsList.length}
              placeholder="Select a Component to add"
              options={Object.values(ComponentEnum)
                .filter((value) => value !== ComponentEnum.LOGIC_COMPONENT)
                .filter((value) => value !== ComponentEnum.FOR_EACH)
                .filter((value) => value !== ComponentEnum.TOGGLE)
                .map((value) => ({
                  value,
                  label: value,
                }))}
              onChange={(option) => addSelectedComponent(option)}
              value={{ value: "", label: "" }}
            />
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="drop-area" direction="vertical">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="border border-dashed border-gray-400 rounded-md p-4 mt-4 space-y-2"
                >
                  {componentsList.length === 0 ? (
                    <p className="text-gray-500">No components added.</p>
                  ) : (
                    componentsList.map((component1, index) => (
                      <div
                        key={component1._id}
                        ref={(el) =>
                          (componentRefs.current[component1._id] = el)
                        }
                      >
                        <DraggableComponentCard
                          component={component1}
                          index={index}
                          handleContentChange={updatecomponentsList}
                          onRemove={() => removeComponent(component1._id)}
                          selectedComponent={selectedComponent}
                          handleComponentClick={handleComponentClick}
                          parentComponent={component}
                          variableUptoCurrentPage={variableUptoCurrentPage}
                          componentRefs={componentRefs}
                          handleTypeChange={handleTypeChange}
                          componentGroup={getComponentGroup(component1.type)}
                          isMinimized={getIsMinimized(component1._id)}
                          setIsMinimized={(isMinimized: boolean) =>
                            setIsMinimized(component1._id, isMinimized)
                          }
                        />
                      </div>
                    ))
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    </Card>
  );
}
